import type { ApiError } from '@td/shared';

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Thin fetch wrapper. Same-origin, so `credentials: 'include'` is all the
 * cookie handling required — there is no token to store anywhere.
 */
/**
 * This tab, for the lifetime of this page.
 *
 * Sent with every change so the live stream can tell a tab its own echo. Not
 * an identity and not a secret — the server only ever compares it to itself,
 * and it is thrown away when the tab closes.
 */
export const CLIENT_ID =
  globalThis.crypto?.randomUUID?.() ?? `tab-${Math.random().toString(36).slice(2)}`;

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      /*
       * FormData sets its own content type, including the multipart boundary
       * the browser generates. Setting one here would strip that boundary and
       * the server would see a single unparseable blob.
       */
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      'X-Client-Id': CLIENT_ID,
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : null;

  if (!res.ok) {
    const err = (body ?? {}) as ApiError;
    throw new ApiRequestError(
      res.status,
      err.error ?? `Request failed (${res.status})`,
      err.details,
    );
  }

  return body as T;
}

export const get = <T>(path: string) => api<T>(path);

export const post = <T>(path: string, data?: unknown) =>
  api<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) });

export const patch = <T>(path: string, data: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(data) });

export const put = <T>(path: string, data: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(data) });

export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** A multipart POST — file uploads. */
export const postForm = <T>(path: string, body: FormData) => api<T>(path, { method: 'POST', body });
